import { Card } from 'pokemon-tcg-sdk-typescript';
import { supabase } from '../lib/supabase';
import type { CardFinish } from '../components/CollectionButton';

const SCRYDEX_TO_COLLECTION_FINISH: Record<string, CardFinish> = {
  normal: 'normal',
  holofoil: 'holofoil',
  reverseHolofoil: 'reverse_holofoil',
  energyReverseHolofoil: 'reverse_holofoil',
  pokeBallReverseHolofoil: 'poke_ball',
  masterBallReverseHolofoil: 'master_ball',
};

function mapScrydexFinish(scrydexFinish: string): CardFinish {
  return SCRYDEX_TO_COLLECTION_FINISH[scrydexFinish] || scrydexFinish;
}

export { mapScrydexFinish as mapScrydexFinishToCollection };

/**
 * Gets the best available price for a card.
 * Primary: Scrydex NM market price. Fallback: market_price_trend_usd.
 */
export function getCardPrice(card: Card): number {
  return card.scrydex_nm_market_price ||
         card.market_price_trend_usd ||
         0;
}

/**
 * Checks if a card has any pricing data available
 */
export function hasCardPrice(card: Card): boolean {
  return getCardPrice(card) > 0;
}

/**
 * Gets the price source description for a card
 */
export function getCardPriceSource(card: Card): string {
  if (card.scrydex_nm_market_price) return 'Scrydex NM';
  if (card.market_price_trend_usd) return 'Market Trend';
  return 'No Price Data';
}

/**
 * Batch-fetch Scrydex NM market prices for an array of card IDs.
 * Prefers the 'normal' finish; falls back to any finish if normal isn't available.
 */
export async function fetchScrydexNmPrices(cardIds: string[], finish?: string): Promise<Map<string, number>> {
  const priceMap = new Map<string, number>();
  if (cardIds.length === 0) return priceMap;

  const PAGE_SIZE = 1000;
  for (let i = 0; i < cardIds.length; i += PAGE_SIZE) {
    const batch = cardIds.slice(i, i + PAGE_SIZE);

    let query = supabase
      .from('card_prices_scrydex_raw')
      .select('card_id, market_price, finish')
      .in('card_id', batch)
      .eq('condition', 'NM');

    if (finish) {
      query = query.eq('finish', finish);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching Scrydex NM prices:', error);
      continue;
    }

    if (data) {
      for (const row of data) {
        if (row.market_price != null) {
          const existing = priceMap.get(row.card_id);
          // Prefer 'normal' finish when no specific finish requested
          if (!finish && existing != null && row.finish !== 'normal') continue;
          priceMap.set(row.card_id, row.market_price);
        }
      }
    }
  }

  return priceMap;
}

/**
 * Enrich an array of cards with Scrydex NM market prices.
 * Uses the 'normal' finish NM price as the default display price.
 */
export async function enrichWithScrydexPrices<T extends { id: string }>(cards: T[]): Promise<T[]> {
  if (cards.length === 0) return cards;

  const cardIds = cards.map(c => c.id);
  const priceMap = await fetchScrydexNmPrices(cardIds);

  if (priceMap.size === 0) return cards;

  return cards.map(card => {
    const nmPrice = priceMap.get(card.id);
    if (nmPrice != null) {
      return { ...card, scrydex_nm_market_price: nmPrice };
    }
    return card;
  });
}

const KNOWN_FINISH_ORDER: CardFinish[] = ['normal', 'holofoil', 'reverse_holofoil', 'poke_ball', 'master_ball'];

function sortFinishes(finishes: Set<CardFinish>): CardFinish[] {
  const known: CardFinish[] = [];
  const extra: CardFinish[] = [];
  for (const f of finishes) {
    if (KNOWN_FINISH_ORDER.includes(f)) known.push(f);
    else extra.push(f);
  }
  known.sort((a, b) => KNOWN_FINISH_ORDER.indexOf(a) - KNOWN_FINISH_ORDER.indexOf(b));
  extra.sort();
  return [...known, ...extra];
}

/**
 * Fetch the available collection finishes for a card from Scrydex pricing data.
 * Known Scrydex finishes are mapped to collection names; unknown ones pass through directly.
 * Returns null if no data is available (caller should show all finishes as fallback).
 */
export async function fetchCardFinishes(cardId: string): Promise<CardFinish[] | null> {
  const { data, error } = await supabase
    .from('card_prices_scrydex_raw')
    .select('finish')
    .eq('card_id', cardId);

  if (error || !data || data.length === 0) return null;

  const scrydexFinishes = [...new Set(data.map((r: { finish: string }) => r.finish))];
  const collectionFinishes = new Set<CardFinish>();

  for (const sf of scrydexFinishes) {
    collectionFinishes.add(mapScrydexFinish(sf));
  }

  if (collectionFinishes.size === 0) return null;

  return sortFinishes(collectionFinishes);
}

/**
 * Batch-fetch available finishes for multiple cards in a single query.
 * Returns a Map from card_id -> CardFinish[].
 * Cards with no Scrydex data will not have an entry in the map.
 */
export async function fetchBatchCardFinishes(cardIds: string[]): Promise<Map<string, CardFinish[]>> {
  const result = new Map<string, CardFinish[]>();
  if (cardIds.length === 0) return result;

  const BATCH_SIZE = 100;
  const allRows: { card_id: string; finish: string }[] = [];

  for (let i = 0; i < cardIds.length; i += BATCH_SIZE) {
    const batch = cardIds.slice(i, i + BATCH_SIZE);
    const { data, error } = await supabase
      .from('card_prices_scrydex_raw')
      .select('card_id, finish')
      .in('card_id', batch)
      .limit(5000);

    if (error) {
      console.error('Error batch-fetching card finishes:', error);
      continue;
    }
    if (data) allRows.push(...data);
  }

  const byCard = new Map<string, Set<string>>();
  for (const row of allRows) {
    if (!byCard.has(row.card_id)) byCard.set(row.card_id, new Set());
    byCard.get(row.card_id)!.add(row.finish);
  }

  for (const [cardId, scrydexFinishes] of byCard) {
    const collectionFinishes = new Set<CardFinish>();
    for (const sf of scrydexFinishes) {
      collectionFinishes.add(mapScrydexFinish(sf));
    }
    if (collectionFinishes.size > 0) {
      result.set(cardId, sortFinishes(collectionFinishes));
    }
  }

  return result;
}